import { useCallback, useEffect, useMemo, useState } from "react";
import { Archive, Clock, Inbox } from "lucide-react";
import { Toaster, toast } from "sonner";
import { Link } from "react-router-dom";
import { api } from "../../lib/apiClient";
import ContactCard from "../../components/ContactCard";

const PAGE_SIZE = 10;

export default function ContactsManager() {
    const [items, setItems] = useState([]);
    const [pageNouveau, setPageNouveau] = useState(1);
    const [pagePrep, setPagePrep] = useState(1);

    const fetchAll = useCallback(async () => {
        try {
            const { data } = await api.get("/admin/contacts");
            setItems(data);
        } catch (_e) {
            toast.error("Erreur de chargement");
        }
    }, []);

    useEffect(() => {
        fetchAll();
    }, [fetchAll]);

    const setStatus = async (id, status) => {
        try {
            await api.put(`/admin/contacts/${id}/status`, { status });
            toast.success(`Marqué ${status === "traite" ? "traité" : status.replace("_", " ")}`);
            fetchAll();
        } catch (_e) {
            toast.error("Erreur");
        }
    };

    const remove = async (id) => {
        if (!window.confirm("Supprimer ce message ?")) return;
        try {
            await api.delete(`/admin/contacts/${id}`);
            toast.success("Supprimé");
            fetchAll();
        } catch (_e) {
            toast.error("Erreur");
        }
    };

    const grouped = useMemo(() => {
        const buckets = { nouveau: [], en_preparation: [], traite: [] };
        for (const m of items) {
            const s = m.status || (m.handled ? "traite" : "nouveau");
            (buckets[s] || buckets.nouveau).push(m);
        }
        // Sort each bucket by created_at desc (most recent first)
        for (const key of Object.keys(buckets)) {
            buckets[key].sort(
                (a, b) => new Date(b.created_at) - new Date(a.created_at)
            );
        }
        return buckets;
    }, [items]);

    const traiteCount = grouped.traite.length;

    return (
        <div data-testid="admin-contacts-page">
            <Toaster richColors position="top-center" />

            <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                    <p className="section-tag">administration</p>
                    <h1 className="font-serif-display text-4xl text-[#2A1408] tracking-tight">
                        Messages reçus
                    </h1>
                </div>
                <Link
                    to="/admin/contacts/traites"
                    className="brand-btn-outline inline-flex items-center gap-2"
                    data-testid="btn-view-traites"
                >
                    <Archive size={16} />
                    Voir les traités ({traiteCount})
                </Link>
            </div>

            <Section
                icon={<Inbox size={20} strokeWidth={2.2} />}
                title="Nouveaux"
                count={grouped.nouveau.length}
                color="text-[#D6552C]"
            >
                <Paginated
                    items={grouped.nouveau}
                    page={pageNouveau}
                    setPage={setPageNouveau}
                    emptyText="Aucun nouveau message."
                    actions={[
                        { label: "En préparation", to: "en_preparation", color: "amber" },
                        { label: "Traité", to: "traite", color: "green" },
                    ]}
                    onSetStatus={setStatus}
                    onRemove={remove}
                />
            </Section>

            <div className="my-6 border-t-2 border-dashed border-[#E4D1BB]" />

            <Section
                icon={<Clock size={20} strokeWidth={2.2} />}
                title="En préparation"
                count={grouped.en_preparation.length}
                color="text-amber-700"
            >
                <Paginated
                    items={grouped.en_preparation}
                    page={pagePrep}
                    setPage={setPagePrep}
                    emptyText="Aucun message en préparation."
                    actions={[
                        { label: "Marquer traité", to: "traite", color: "green" },
                        { label: "Remettre nouveau", to: "nouveau", color: "neutral" },
                    ]}
                    onSetStatus={setStatus}
                    onRemove={remove}
                />
            </Section>
        </div>
    );
}

export function Section({ icon, title, count, color, children }) {
    return (
        <section className="mt-8">
            <div className={`flex items-center gap-2 mb-3 ${color}`}>
                {icon}
                <h2 className="font-display text-2xl">
                    {title} <span className="opacity-70 text-base">({count})</span>
                </h2>
            </div>
            <div className="space-y-3">{children}</div>
        </section>
    );
}

export function Paginated({ items, page, setPage, emptyText, actions, onSetStatus, onRemove, muted = false }) {
    if (items.length === 0) {
        return <p className="text-[#5C3D2E] text-sm pl-1">{emptyText}</p>;
    }
    const totalPages = Math.max(1, Math.ceil(items.length / PAGE_SIZE));
    const safePage = Math.min(page, totalPages);
    const start = (safePage - 1) * PAGE_SIZE;
    const slice = items.slice(start, start + PAGE_SIZE);
    return (
        <>
            {slice.map((m) => (
                <ContactCard
                    key={m.id}
                    m={m}
                    onSetStatus={onSetStatus}
                    onRemove={onRemove}
                    actions={actions}
                    muted={muted}
                />
            ))}
            {totalPages > 1 && (
                <div className="flex items-center justify-between pt-3 text-sm">
                    <button
                        type="button"
                        onClick={() => setPage(safePage - 1)}
                        disabled={safePage <= 1}
                        className="brand-btn-outline disabled:opacity-40 px-3 py-1.5 text-xs"
                    >
                        Précédent
                    </button>
                    <span className="text-[#5C3D2E]">
                        Page {safePage} / {totalPages}
                    </span>
                    <button
                        type="button"
                        onClick={() => setPage(safePage + 1)}
                        disabled={safePage >= totalPages}
                        className="brand-btn-outline disabled:opacity-40 px-3 py-1.5 text-xs"
                    >
                        Suivant
                    </button>
                </div>
            )}
        </>
    );
}
